import { useTranslation } from "react-i18next";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SurveyQuestion, SurveyQuestionFormula } from "../../../types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../../redux/store";
import { useMemo, useState } from "react";
import { AnswerTypeEnum, FormulaOpetationEnum } from "../../../types/enum";
import { validateQuestionFormula } from "../../../utils/validateQuestionFormula";
import { setError } from "../../../../../../redux/surveySlice";

interface SurveyQuestionAnswerProps {
    index: number;
    updateQuestionField: (index: number, field: keyof SurveyQuestion, value: any) => void;
}

export default function SurveyQuestionAnswerFormula({ index, updateQuestionField }: SurveyQuestionAnswerProps) {
    const { t } = useTranslation(['surveyAdmin', 'common']);

    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.surveyForm.errors);
    const question = useSelector((state: RootState) => state.surveyForm.data.questions[index]);
    const questions = useSelector((state: RootState) => state.surveyForm.data.questions) ?? [];

    const [ formulaVariables, setFormulaVariables ] = useState<SurveyQuestionFormula>({ });

    const allowedCodes = useMemo(
        () => questions.map((q) => q.code).filter((code): code is string => !!code),
        [questions]
    );
    const formulaErrorKey = useMemo(
        () => validateQuestionFormula(question.formula ?? "", allowedCodes),
        [question.formula, allowedCodes]
    );
    const formulaError = formulaErrorKey
        ? t(`surveyAdmin:form.formulaError.${formulaErrorKey}`)
        : null;

    const addIntoFormula = () => {
        const parts: string[] = [];

        if (formulaVariables.question) {
            parts.push(`[PREG-${formulaVariables.question}]`);
        }

        let opText = "";
        if (formulaVariables.operation !== undefined && formulaVariables.operation !== null) {
            opText = FormulaOpetationEnum[formulaVariables.operation as number];
            parts.push(opText ?? String(formulaVariables.operation));
        }

        if (formulaVariables.number !== undefined && formulaVariables.number !== null) {
            parts.push(String(formulaVariables.number));
        }

        if (parts.length === 0) {
            return;
        }
        const segment =
            formulaVariables.question && opText === "("
                ? `[PREG-${formulaVariables.question}] * (`
                : parts.join(" ");
        const current = (question.formula ?? "").trim();
        const nextFormula = current ? `${current} ${segment}` : segment;

        setFormulaVariables({});
        updateQuestionField(index, "formula", nextFormula);
        dispatch(setError({ field: `questions[${index}].formula`, message: undefined }));
    }

    return (
        <Row>
            <Col md={9}>
                <Row>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("surveyAdmin:form.question")}</Form.Label>
                        <Form.Select
                            as="select"
                            value={formulaVariables?.question ?? ""}
                            onChange={(e) => { setFormulaVariables(prev => ({...prev, question: e.target.value})) }}
                        >
                            <option value=""></option>
                            {questions
                                .filter((question) => Number(question.answerType) == AnswerTypeEnum.Option && question.code && question.isAnswerMandatory)
                                .map((question, index) => (
                                <option key={index} value={question.code}>
                                    {question.code} 
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("surveyAdmin:form.operation")}</Form.Label>
                        <Form.Select
                            as="select"
                            value={formulaVariables.operation !== undefined && formulaVariables.operation !== null
                                ? String(formulaVariables.operation)
                                : ""
                            }
                            onChange={(e) => {
                                const raw = e.target.value;
                                const value = raw === "" ? undefined : Number(raw);
                                setFormulaVariables(prev => ({...prev, operation: value}));
                            }}
                        >
                            <option value=""></option>
                            {Object.values(FormulaOpetationEnum).filter((key) => isNaN(Number(key))).map((name, index) => {
                                const value = FormulaOpetationEnum[name as keyof typeof FormulaOpetationEnum];
                                return (<option key={index} value={value}>
                                    {name} 
                                </option>)
                            })}
                        </Form.Select>
                    </Form.Group>
                </Row>
                <Row>
                    <Col md={8}>
                        <Form.Group>
                            <Form.Label>{t("surveyAdmin:form.numbers")}</Form.Label>
                            <Form.Control
                                type="number"
                                max={100}
                                value={formulaVariables.number ?? ""}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setFormulaVariables(prev => ({...prev, number: value}))
                                }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="d-flex justify-content-end align-items-end">
                        <Button
                            variant="primary"
                            onClick={addIntoFormula}
                        >
                            {t("surveyAdmin:form.addIntoFormula")}
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("surveyAdmin:form.formula")} *</Form.Label>
                        <Form.Control
                            value={question.formula ?? ""}
                            onChange={(e) => {
                                updateQuestionField(index, 'formula', e.target.value);
                                dispatch(setError({ field: `questions[${index}].formula`, message: undefined }));
                            }}
                            isInvalid={!!errors[`questions[${index}].formula`]}
                        />
                        {formulaError
                            ? <Form.Text className="text-danger">
                                {formulaError}
                            </Form.Text> 
                            : <Form.Control.Feedback type="invalid">
                                {errors[`questions[${index}].formula`]}
                            </Form.Control.Feedback>
                        }
                    </Form.Group>
                </Row>
            </Col>
        </Row>
    );
}
